// gpsStatus.js
//
// Derives the GPS status shown in the app from the Fixposition FUSION state
// rather than from the two GNSS receivers alone.
//
// Why this exists
// ---------------
// The robot-side aggregator builds `gps_status` from gnss1_status/gnss2_status
// only. Those describe the two GNSS *receivers*, not the fused solution — so
// when the VRTK2 fusion engine is uninitialised the app happily displayed
// "RTK Fixed" while `/fixposition/odometry_llh` was publishing latitude 0,
// longitude 0. Observed in the field 2026-08-12: both antennas reported RTK
// while init_status was 0, the map flew to Null Island, and cross-track error
// read ~183 million inches. The status actively lied at the moment it mattered
// most.
//
// The position the app plots comes from the FUSED output, so the status must
// describe the fused solution. init_status is the gate; the antenna states are
// detail shown underneath so an operator can see *why* it is degraded.
//
// Enum values are from Fixposition's FP_A-ODOMSTATUS spec:
//   https://docs.fixposition.com/fd/fp_a-odomstatus

// init_status: 1 is documented as "reserved", so 2 is the ONLY good value.
export const INIT_NOT_INITIALIZED = 0;
export const INIT_GLOBALLY_INITIALIZED = 2;

// gnss1_status / gnss2_status
export const GNSS_NO_FIX = 0;
export const GNSS_SPP = 1;
export const GNSS_RTK_MOVING_BASELINE = 2;
export const GNSS_RTK_FLOAT = 5;
export const GNSS_RTK_FIXED = 8;

// Short labels for the per-antenna detail line. NOTE the real values: RTK float
// is 5, not 7 — the aggregator's `gnss == 7` branch can never fire, which is why
// "RTK Float" never appeared in the app.
const ANTENNA_SHORT = {
  [GNSS_NO_FIX]: 'none',
  [GNSS_SPP]: 'spp',
  [GNSS_RTK_MOVING_BASELINE]: 'mb',
  [GNSS_RTK_FLOAT]: 'float',
  [GNSS_RTK_FIXED]: 'fix',
};

// corr_status — only 5 means corrections are fully applied.
export const CORR_SUFFICIENT = 5;
const CORR_SHORT = {
  0: 'waiting',
  1: 'no gnss',
  2: 'none',
  3: 'limited',
  4: 'stale',
  5: 'ok',
};

const antennaLabel = (v) => ANTENNA_SHORT[v] ?? `?${v}`;
const corrLabel = (v) => CORR_SHORT[v] ?? `?${v}`;

/**
 * Derive what the GPS Status tile should show.
 *
 * @param fusion   latest /fixposition/fpa/odomstatus message, or null/undefined
 *                 when the topic has not been seen (older robot build, or the
 *                 driver is down).
 * @param fallback the aggregator's `gps_status` string, used only when we have
 *                 no fusion data at all.
 * @returns {{label: string, detail: string, level: 'good'|'warn'|'bad', fusionKnown: boolean}}
 *          `level` drives the tile colour so callers never string-match labels.
 */
export function deriveGpsStatus(fusion, fallback = 'No Data') {
  // No fusion data — degrade to whatever the robot told us, but say so, because
  // an unqualified "RTK Fixed" here is exactly the claim we cannot verify.
  if (!fusion || typeof fusion.init_status !== 'number') {
    const known = typeof fallback === 'string' && fallback.length > 0;
    return {
      label: known ? fallback : 'No Data',
      detail: 'fusion state unknown',
      level: fallback === 'RTK Fixed' ? 'warn' : 'bad',
      fusionKnown: false,
    };
  }

  const g1 = fusion.gnss1_status;
  const g2 = fusion.gnss2_status;
  const ants = `ant ${antennaLabel(g1)}/${antennaLabel(g2)}`;
  const corr = typeof fusion.corr_status === 'number'
    ? ` · corr ${corrLabel(fusion.corr_status)}`
    : '';

  // THE GATE. Until fusion is globally initialised the fused position is not
  // usable no matter how good the antennas look.
  if (fusion.init_status !== INIT_GLOBALLY_INITIALIZED) {
    return {
      label: 'NO FUSION',
      detail: `not initialised · ${ants}${corr}`,
      level: 'bad',
      fusionKnown: true,
    };
  }

  // Fusion is initialised — now the antenna quality decides how good it is.
  const best = Math.max(
    typeof g1 === 'number' ? g1 : -1,
    typeof g2 === 'number' ? g2 : -1,
  );

  if (g1 === GNSS_RTK_FIXED && g2 === GNSS_RTK_FIXED) {
    return { label: 'RTK Fixed', detail: `${ants}${corr}`, level: 'good', fusionKnown: true };
  }
  if (best === GNSS_RTK_FIXED) {
    return { label: 'One Fix', detail: `${ants}${corr}`, level: 'warn', fusionKnown: true };
  }
  if (best === GNSS_RTK_FLOAT) {
    return { label: 'RTK Float', detail: `${ants}${corr}`, level: 'warn', fusionKnown: true };
  }
  if (best === GNSS_SPP || best === GNSS_RTK_MOVING_BASELINE) {
    return { label: 'SPP', detail: `${ants}${corr}`, level: 'warn', fusionKnown: true };
  }
  return { label: 'No Fix', detail: `${ants}${corr}`, level: 'bad', fusionKnown: true };
}

/** Tile background for a status level, matching the existing palette. */
export function gpsLevelColor(level, isStale) {
  if (isStale) return 'rgba(180, 180, 180, 0.75)';
  if (level === 'good') return 'rgba(0, 255, 0, 0.75)';
  if (level === 'warn') return 'rgba(255, 255, 0, 0.75)';
  return 'rgba(255, 0, 0, 0.75)';
}
